import { SIZE_SELECTED, SIZE_DESELECTED } from 'app/productDetails/productDetailsConstants';
import { ajaxRequest } from 'app/utils/helpers';
import { dlTrackProductsReceive, dlViewPLPOrSRPItemList } from 'app/analytics/productListAnalytics';
import {
  displayFooter,
  getGlobalWishlistInfo,
  getLoggedUserInfo,
  loadNotifyMeClearanceStatuses,
} from 'app/utils/globalDataHelpers';
import { setQueryInUrl, applyRedirects, prepareQueryParams, inWishlistProducts } from './productListUtils';
import { fetchNoResults } from '../noResults/noResultsActions';
import {
  REQUEST_PRODUCTS,
  REQUEST_PREPEND_PRODUCTS,
  REQUEST_APPEND_PRODUCTS,
  PREPEND_PRODUCTS,
  APPEND_PRODUCTS,
  RECEIVE_PRODUCTS,
  INITIAL_PRODUCTS_DATA,
  LOADER_TIMEOUT,
  UPDATE_PRODUCT_DETAILS,
  UPDATE_IN_WISHLIST_PRODUCTS,
  UPDATE_DISPLAYED_ITEMS,
  RECEIVE_PRODUCTS_ERROR,
} from './productListConstants';
import { getValidityOfAppliedFacets } from '../../productsReducer';
import { getCleanFilters, getSessionId, getTemplateName, mapAdvancedCommerceFacets } from './advancedCommerce/utils';
import { mapAdvancedCommerceResponse } from './advancedCommerce/mapAdvancedCommerceResponse';
import { mockedSortFields } from './advancedCommerce/constants';
import { performAdvancedSearch } from './advancedCommerce/performAdvancedSearch';
import { redirectTo } from '../../../utils/redirects';

const { searchPageUrl } = window.inlineSearchConfiguration;
const { searchUrl, pageFetchSize, categoryId } = window.inlinePlp || {};
const {
  advancedCommerce: { isListingPage },
  locale,
} = window.inlineGlobalConfig;

const requestProducts = showLoader => ({
  type: REQUEST_PRODUCTS,
  showLoader,
});

const receiveProducts = (json = INITIAL_PRODUCTS_DATA) => ({
  type: RECEIVE_PRODUCTS,
  products: json.products,
  searchMetadata: json.searchMetadata,
  page: json.page,
  willUpdateAvailableFacets: json.willUpdateAvailableFacets,
  mustValidateURLParams: json.mustValidateURLParams,
});

const receiveAdditionalProducts =
  ({ products, page, prepend }) =>
  async dispatch => {
    await loadNotifyMeClearanceStatuses();
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(
          dispatch({
            type: prepend ? PREPEND_PRODUCTS : APPEND_PRODUCTS,
            products,
            page,
          })
        );
      }, LOADER_TIMEOUT);
    });
  };

export const receiveProductsAfterAnimationTimeout =
  (data, isTimerNeeded = false) =>
  async dispatch => {
    await loadNotifyMeClearanceStatuses();
    return new Promise(resolve => {
      setTimeout(
        () => {
          resolve(dispatch(receiveProducts(data)));
        },
        isTimerNeeded ? LOADER_TIMEOUT : 0
      );
    });
  };

const handleRedirects = json => {
  const isSearchResult = window.location.href.split('?')[0].includes(searchPageUrl);
  return isSearchResult && applyRedirects(json);
};

const handleNoResults = isTimerNeeded => (dispatch, getState) => {
  dispatch(receiveProductsAfterAnimationTimeout(INITIAL_PRODUCTS_DATA, isTimerNeeded));
  // TODO implement computing of noResultsQuery from validAppliedFacets
  // ex: Material: Leather,...; Color Group: Brown; Size: 7,8
  // eslint-disable-next-line
  const { isValid, ...validAppliedFacets } = getValidityOfAppliedFacets(getState());
  return dispatch(fetchNoResults());
};

export const updateDisplayedItems = displayedItems => ({
  type: UPDATE_DISPLAYED_ITEMS,
  displayedItems,
});

export const fetchProducts =
  ({
    showLoader = false,
    willUpdateAvailableFacets = true,
    mustValidateURLParams = false,
    withAnalytics = false,
  } = {}) =>
  async (dispatch, getState) => {
    const { visiblePages } = getState();
    dispatch(requestProducts(showLoader));

    if (isListingPage) {
      try {
        const state = getState();
        const { selectedSort } = state;

        const template = getTemplateName(categoryId);

        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('query') || '';

        if ((!query || !query.trim()) && template === 'search') {
          return dispatch(handleNoResults(null, showLoader)).then(displayFooter);
        }

        const appliedFilters = getCleanFilters(state.filters.applied.itemsPerFacet);

        let uniqueUserId;
        const userInfo = await getLoggedUserInfo();

        if (userInfo?.['userInfo/index']?.encodedId) {
          uniqueUserId = userInfo['userInfo/index'].encodedId;
        } else {
          uniqueUserId = getSessionId();
        }

        const userForSearch = { id: uniqueUserId };

        const fullResponse = await performAdvancedSearch(
          query,
          locale.currentCurrency,
          userForSearch,
          appliedFilters,
          selectedSort,
          1,
          24,
          categoryId,
          dispatch
        );

        if (fullResponse.header?.redirect_url) {
          return redirectTo(fullResponse.header.redirect_url);
        }

        const searchData = fullResponse.elements?.[template];

        const noItemsFound = !searchData?.products?.meta || searchData.products.meta.count === 0;

        if (noItemsFound) {
          document.querySelector('.header__promo-wrapper').style.display = 'none';
          dispatch(handleNoResults(searchData?.products?.facets, showLoader)).then(displayFooter);
          return false;
        }

        const wishlistInfo = await getGlobalWishlistInfo();
        const formattedData = mapAdvancedCommerceResponse(fullResponse);

        const finalProducts = inWishlistProducts(wishlistInfo.wishlist, formattedData.products);

        const mappedFacets = mapAdvancedCommerceFacets(fullResponse);

        const searchMetadata = {
          totalItems: searchData.products.meta.count,
          facets: mappedFacets,
          sortFields: mockedSortFields,
        };

        await dispatch(
          receiveProductsAfterAnimationTimeout(
            {
              products: finalProducts,
              searchMetadata,
              sortField: mockedSortFields,
              page: searchData.products.meta.current_page - 1,
              willUpdateAvailableFacets,
              mustValidateURLParams,
            },
            showLoader
          )
        );

        setQueryInUrl(getState());

        if (withAnalytics) {
          dlTrackProductsReceive(finalProducts, searchMetadata.totalItems);
          dlViewPLPOrSRPItemList(finalProducts);
        }

        return fullResponse;
      } catch (error) {
        console.error('Advanced Commerce search failed:', error);
        dispatch(receiveProductsAfterAnimationTimeout());
        return false;
      }
    } else {
      return ajaxRequest(
        'GET',
        searchUrl,
        prepareQueryParams(
          getState,
          visiblePages.first * pageFetchSize,
          (visiblePages.last - visiblePages.first + 1) * pageFetchSize
        )
      ).then(
        json => {
          const noItemsFound = !json.searchMetadata || json.searchMetadata.totalItems === 0;
          const isRedirectApplied = handleRedirects(json);

          if (isRedirectApplied) {
            // Hide search banner completely when redirect applies
            document.querySelector('.header__promo-wrapper').style.display = 'none';
            dispatch(receiveProductsAfterAnimationTimeout());
            return false;
          }

          if (noItemsFound) {
            // Hide search banner completely for no results page
            document.querySelector('.header__promo-wrapper').style.display = 'none';
            dispatch(handleNoResults(json.searchMetadata && json.searchMetadata.facets, showLoader)).then(
              displayFooter
            );
            return false;
          }

          return getGlobalWishlistInfo().then(response => {
            return dispatch(
              receiveProductsAfterAnimationTimeout(
                {
                  products: inWishlistProducts(response.wishlist, json.products),
                  searchMetadata: json.searchMetadata,
                  page: visiblePages.last,
                  willUpdateAvailableFacets,
                  mustValidateURLParams,
                },
                showLoader
              )
            ).then(() => {
              setQueryInUrl(getState());

              if (withAnalytics) {
                dlTrackProductsReceive(json.products, json.searchMetadata.totalItems);
                dlViewPLPOrSRPItemList(json.products);
              }

              return json;
            });
          });
        },
        () => dispatch(receiveProductsAfterAnimationTimeout())
      );
    }
  };

export const fetchAdditionalProducts =
  ({ prepend = false, withAnalytics } = {}) =>
  async (dispatch, getState) => {
    if (prepend) return;

    if (isListingPage) {
      dispatch({ type: REQUEST_APPEND_PRODUCTS });
      try {
        const state = getState();
        const { products, selectedSort, filters } = state;
        const { items } = products;

        const defaultItemsPerPage = 24;

        const nextPage = Math.floor(items.length / defaultItemsPerPage) + 1;

        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('query') || '';

        const appliedFilters = getCleanFilters(filters.applied.itemsPerFacet);
        const userForSearch = { id: getSessionId() };

        const fullResponse = await performAdvancedSearch(
          query,
          locale.currentCurrency,
          userForSearch,
          appliedFilters,
          selectedSort,
          nextPage,
          24,
          categoryId,
          dispatch
        );

        const template = getTemplateName(categoryId);

        if (!fullResponse?.elements?.[template]?.products?.results) return;

        const formattedData = mapAdvancedCommerceResponse(fullResponse);
        const newProducts = inWishlistProducts((await getGlobalWishlistInfo()).wishlist, formattedData.products);

        if (newProducts.length > 0) {
          await dispatch(receiveAdditionalProducts({ products: newProducts, page: nextPage, prepend: false }));
          const newTotalLoaded = getState().products.items.length;
          dispatch(updateDisplayedItems(newTotalLoaded));

          if (withAnalytics) dlViewPLPOrSRPItemList(newProducts);
        }
      } catch (error) {
        console.error('Failed to fetch additional products:', error);
      }
    } else {
      const { visiblePages } = getState();
      const targetPage = prepend ? visiblePages.first - 1 : visiblePages.last + 1;
      dispatch({
        type: prepend ? REQUEST_PREPEND_PRODUCTS : REQUEST_APPEND_PRODUCTS,
      });
      const offset = pageFetchSize * targetPage;
      const apiQueryParams = prepareQueryParams(getState, offset, pageFetchSize);
      ajaxRequest('GET', searchUrl, apiQueryParams).then(json => {
        return getGlobalWishlistInfo().then(response => {
          dispatch(
            receiveAdditionalProducts({
              products: inWishlistProducts(response.wishlist, json.products),
              page: targetPage,
              prepend,
            })
          ).then(() => {
            if (withAnalytics) {
              dlViewPLPOrSRPItemList(json.products);
            }
          });
          return json;
        });
      });
    }
  };

export const fetchAllProducts = () => async (dispatch, getState) => {
  if (isListingPage) {
    dispatch(requestProducts(true));
    try {
      const state = getState();
      const { products, selectedSort, filters } = state;
      const { totalItems } = products;

      if (!totalItems) return;

      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('query') || '';

      const appliedFilters = getCleanFilters(filters.applied.itemsPerFacet);
      const userForSearch = { id: getSessionId() };

      const fullResponse = await performAdvancedSearch(
        query,
        locale.currentCurrency,
        userForSearch,
        appliedFilters,
        selectedSort,
        1,
        totalItems,
        categoryId,
        dispatch
      );

      const template = getTemplateName(categoryId);

      if (!fullResponse?.elements?.[template]?.products?.results) return;

      const formattedData = mapAdvancedCommerceResponse(fullResponse);
      const allProducts = inWishlistProducts((await getGlobalWishlistInfo()).wishlist, formattedData.products);

      dispatch(
        receiveProducts({
          products: allProducts,
          searchMetadata: {
            ...formattedData.searchMetadata,
            facets: mapAdvancedCommerceFacets(fullResponse),
            sortFields: mockedSortFields,
          },
          sortFields: mockedSortFields,
          page: 0,
          willUpdateAvailableFacets: true,
        })
      );
      dispatch(updateDisplayedItems(allProducts.length));
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      dispatch({
        type: RECEIVE_PRODUCTS_ERROR,
      });
    }
  }
};

const notFoundHandler = error => ({
  status: 404,
  redirectHandler() {
    throw Error(`${error} (404 Error)`);
  },
});

export const fetchProductDetails = (productUrl, productId, productType) => dispatch => {
  const dynamicProductUrl = productUrl.replace(/-p-/, '-dyn-');

  Promise.all([
    ajaxRequest('GET', productUrl, {}, notFoundHandler('Product not found')),
    ajaxRequest('GET', dynamicProductUrl, {}, notFoundHandler('Product not found')),
  ]).then(productJsons => {
    const productData = { ...productJsons[0], ...productJsons[1] };
    const skus = productData && productData.skus && productData.skus.some ? productData.skus : null;
    const hasStock = skus && !!skus.some(sku => sku.stock > 0);

    return dispatch({
      type: UPDATE_PRODUCT_DETAILS,
      productDetails: { ...productData, hasStock },
      productId,
      productType,
    });
  });
};

export const sizeSelected = (selectedSize, productId) => ({
  type: SIZE_SELECTED,
  selectedSize,
  productId,
});

export const sizeDeselected = (selectedSize, productId) => ({
  type: SIZE_DESELECTED,
  selectedSize,
  productId,
});

export const addProductToWishlistOnPlp = (product, updateScreen) => () => {
  setTimeout(() => updateScreen('wishlistConfirmation'), 1000);
};

export const fetchProductsOnInfiniteScroll = () => (dispatch, getState) => {
  const { products, visiblePages } = getState();

  const totalPages = Math.ceil(products.totalItems / pageFetchSize);
  if (visiblePages.last < totalPages - 1) {
    dispatch(fetchAdditionalProducts({ withAnalytics: true }));
  }
};

const markProductInWishlist = (items, productId, inWishlist) =>
  items.map(item => (item.id === productId ? { ...item, inWishlist } : item));

export const updateInWishlistProducts = response => (dispatch, getState) => {
  const hasValidWishlistData = response && response.wishlist && response.wishlist.entries;

  if (hasValidWishlistData) {
    return dispatch({
      type: UPDATE_IN_WISHLIST_PRODUCTS,
      products: inWishlistProducts(response.wishlist, getState().products.items),
    });
  }

  if (response && response.addedProductId) {
    return dispatch({
      type: UPDATE_IN_WISHLIST_PRODUCTS,
      products: markProductInWishlist(getState().products.items, response.addedProductId, true),
    });
  }

  if (response && response.removedProductId) {
    return dispatch({
      type: UPDATE_IN_WISHLIST_PRODUCTS,
      products: markProductInWishlist(getState().products.items, response.removedProductId, false),
    });
  }

  return getGlobalWishlistInfo().then(globalResponse => {
    if (globalResponse && globalResponse.wishlist && globalResponse.wishlist.entries) {
      return dispatch({
        type: UPDATE_IN_WISHLIST_PRODUCTS,
        products: inWishlistProducts(globalResponse.wishlist, getState().products.items),
      });
    }
    return null;
  });
};
